import React, { useEffect, useMemo, useState } from "react";
import MasterLayout from "../../masterLayout/MasterLayout";
import { QrReader } from "react-qr-reader";
import { toast } from "react-toastify";
import { apiFetch } from "../../utils/api";

const normalizarPreview = (data) => {
  const cliente = data?.cliente || null;
  const nombreCompleto = cliente
    ? [cliente.nombre, cliente.apellido].filter(Boolean).join(" ").trim()
    : "";

  return {
    cliente: cliente
      ? {
          ...cliente,
          ID_CLI: cliente.id,
          NOMBRES_DP: cliente.nombre || nombreCompleto,
          APELLIDO_PATERNO_DP: cliente.apellido || "",
          APELLIDO_MATERNO_DP: "",
          FOTO_DP: cliente.photoUrl || "",
        }
      : null,
    partidas: Array.isArray(data?.partidas)
      ? data.partidas.map((partida) => {
          const fecha = partida.fecha ? new Date(partida.fecha) : null;
          const fin = fecha
            ? new Date(fecha.getTime() + (partida.duracion || 90) * 60 * 1000)
            : null;
          return {
            ...partida,
            ID_MAT: partida.id,
            DIA_MAT: partida.fecha,
            INICIO_HORA: fecha ? fecha.toTimeString().slice(0, 5) : "",
            FIN_HORA: fin ? fin.toTimeString().slice(0, 5) : "",
            NOMBRE_CAN: partida.cancha?.nombre || partida.court?.nombre || "Cancha",
            NUMERO_CANCHA_CAN: "",
            NOMBRE_SUCURSAL_SUC: partida.sucursal?.nombre || partida.branch?.nombre || "Sucursal",
            ESTATUS_DESCRIPCION: partida.status === "closed" ? "Finalizada" : "Programada",
            ROLE_MAC: partida.createdByUserId === cliente?.id ? "CREATOR" : "PLAYER",
            YA_REGISTRADO: Boolean(partida.asistio),
          };
        })
      : [],
  };
};

const PaseListaPartidas = () => {
  const [showScanner, setShowScanner] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [confirmingMatchId, setConfirmingMatchId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [qrToken, setQrToken] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [lastScannedValue, setLastScannedValue] = useState("");

  const cliente = preview?.cliente || null;
  const partidas = preview?.partidas || [];

  const inicialesCliente = useMemo(() => {
    if (!cliente) return "QR";
    const nombres = [
      cliente.NOMBRES_DP,
      cliente.APELLIDO_PATERNO_DP,
      cliente.APELLIDO_MATERNO_DP,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    return nombres
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((parte) => parte[0]?.toUpperCase())
      .join("") || "QR";
  }, [cliente]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const match = timeString.match(/(\d{1,2}):(\d{2})/);
    return match ? `${match[1].padStart(2, "0")}:${match[2]}` : timeString;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPhotoUrl = (foto) => {
    if (!foto || typeof foto !== "string") return "";
    return foto.trim();
  };

  const resetPreview = () => {
    setPreview(null);
    setErrorMessage("");
    setLastScannedValue("");
  };

  const cargarHistorial = async () => {
    setIsLoadingHistory(true);
    try {
      const data = await apiFetch("/api/check-in/history?limit=30");
      if (data.status) {
        setHistorial(Array.isArray(data.data) ? data.data : []);
      } else {
        setHistorial([]);
      }
    } catch (error) {
      setHistorial([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    cargarHistorial();
  }, []);

  const cargarPreview = async (token) => {
    setIsLoadingPreview(true);
    setErrorMessage("");

    try {
      const data = await apiFetch("/api/check-in/preview", {
        method: "POST",
        body: JSON.stringify({ token }),
      });

      if (data.status) {
        setPreview(normalizarPreview(data.data));
        setQrToken(token);
      } else {
        setPreview(null);
        setQrToken("");
        setErrorMessage(data.message || "No fue posible leer el QR.");
      }
    } catch (error) {
      setPreview(null);
      setQrToken("");
      setErrorMessage("Ocurrió un error al consultar el QR.");
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleScan = async (result) => {
    const scannedText = result?.text?.trim();
    if (!scannedText || isLoadingPreview || scannedText === lastScannedValue) {
      return;
    }

    setLastScannedValue(scannedText);
    setShowScanner(false);
    await cargarPreview(scannedText);
  };

  const confirmarAsistencia = async (idPartida) => {
    if (!cliente) return;

    setConfirmingMatchId(idPartida);
    try {
      const data = await apiFetch("/api/check-in/confirm", {
        method: "POST",
        body: JSON.stringify({
          token: qrToken,
          matchIds: [idPartida],
        }),
      });

      if (data.status) {
        const registradas = data.data?.registradas ?? 0;
        if (registradas === 0) {
          toast.info(data.message || "La asistencia ya fue registrada");
        } else {
          toast.success(data.message || "Asistencia registrada");
        }
        if (qrToken) {
          await cargarPreview(qrToken);
        }
        await cargarHistorial();
      } else {
        toast.error(data.message || "No fue posible registrar la asistencia");
      }
    } catch (error) {
      toast.error("Ocurrió un error al registrar la asistencia");
    } finally {
      setConfirmingMatchId(null);
    }
  };

  return (
    <MasterLayout>
      <div className="card h-100 p-0 radius-12 overflow-hidden">
        <div className="card-body p-40">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <div>
              <h4 className="mb-1">Pase de lista</h4>
              <p className="text-muted mb-0">
                Escanea el QR del jugador para consultar su reserva, su cancha y registrar asistencia.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  resetPreview();
                  setShowScanner(false);
                }}
              >
                Limpiar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  resetPreview();
                  setShowScanner(true);
                }}
              >
                {showScanner ? "Escaneando..." : "Escanear QR"}
              </button>
            </div>
          </div>

          {showScanner && (
            <div className="card border mb-4">
              <div className="card-body">
                <h6 className="mb-3">Escáner QR</h6>
                <QrReader
                  constraints={{ facingMode: "environment" }}
                  onResult={(result) => handleScan(result)}
                  containerStyle={{ width: "100%" }}
                  videoStyle={{ width: "100%" }}
                />
                <div className="text-end mt-3">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => setShowScanner(false)}
                  >
                    Detener escaneo
                  </button>
                </div>
              </div>
            </div>
          )}

          {isLoadingPreview && (
            <div className="text-center py-5">
              <div
                className="spinner-border text-primary"
                role="status"
                aria-hidden="true"
              ></div>
              <p className="mt-3 mb-0 text-muted">Consultando partidas del día...</p>
            </div>
          )}

          {!isLoadingPreview && errorMessage && (
            <div className="alert alert-warning" role="alert">
              {errorMessage}
            </div>
          )}

          {!isLoadingPreview && !showScanner && !cliente && !errorMessage && (
            <div className="text-center py-5">
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
                style={{ width: 88, height: 88, backgroundColor: "#EEF6E0" }}
              >
                <i className="ri-qr-scan-line text-primary" style={{ fontSize: 36 }}></i>
              </div>
              <h5 className="mb-2">Escanea un jugador para comenzar</h5>
              <p className="text-muted mb-0">
                Mostraremos su nombre, foto, cancha, sucursal y las partidas que
                tiene hoy para confirmar la asistencia.
              </p>
            </div>
          )}

          {!isLoadingPreview && cliente && (
            <div className="row g-4">
              <div className="col-12">
                <div className="card border radius-12">
                  <div className="card-body d-flex flex-wrap align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
                      style={{
                        width: 84,
                        height: 84,
                        backgroundColor: "#89A5CE",
                        backgroundImage: getPhotoUrl(cliente.FOTO_DP)
                          ? `url(${getPhotoUrl(cliente.FOTO_DP)})`
                          : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    >
                      {getPhotoUrl(cliente.FOTO_DP) ? "" : inicialesCliente}
                    </div>
                    <div>
                      <h4 className="mb-1">
                        {[
                          cliente.NOMBRES_DP,
                          cliente.APELLIDO_PATERNO_DP,
                          cliente.APELLIDO_MATERNO_DP,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </h4>
                      <p className="text-muted mb-0">
                        ID jugador: {cliente.ID_CLI}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="card border radius-12">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="mb-1">Partidas de hoy</h5>
                        <p className="text-muted mb-0">
                          {partidas.length === 0
                            ? "El jugador no tiene partidas registradas hoy."
                            : "Confirma la asistencia por cada partida correspondiente."}
                        </p>
                      </div>
                    </div>

                    {partidas.length === 0 ? (
                      <div className="text-center py-4">
                        <i
                          className="ri-calendar-event-line text-muted"
                          style={{ fontSize: 36 }}
                        ></i>
                        <p className="mt-3 mb-0 text-muted">
                          No tiene partidas hoy.
                        </p>
                      </div>
                    ) : (
                      <div className="row g-3">
                        {partidas.map((partida) => (
                          <div className="col-12" key={partida.ID_MAT}>
                            <div className="border rounded-3 p-3">
                              <div className="d-flex flex-wrap justify-content-between gap-3">
                                <div>
                                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                    <span className="badge text-bg-light">
                                      {formatDate(partida.DIA_MAT)}
                                    </span>
                                    <span className="badge text-bg-secondary">
                                      {formatTime(partida.INICIO_HORA)} -{" "}
                                      {formatTime(partida.FIN_HORA)}
                                    </span>
                                    <span
                                      className={`badge ${
                                        partida.ESTATUS_DESCRIPCION === "En juego"
                                          ? "text-bg-success"
                                          : partida.ESTATUS_DESCRIPCION === "Finalizada"
                                          ? "text-bg-dark"
                                          : "text-bg-warning"
                                      }`}
                                    >
                                      {partida.ESTATUS_DESCRIPCION}
                                    </span>
                                    <span className="badge text-bg-info">
                                      {partida.ROLE_MAC === "CREATOR"
                                        ? "Creador"
                                        : "Jugador"}
                                    </span>
                                    {partida.YA_REGISTRADO ? (
                                      <span className="badge text-bg-primary">
                                        Ya registrado
                                      </span>
                                    ) : null}
                                  </div>
                                  <h6 className="mb-1">
                                    {partida.NOMBRE_CAN} #{partida.NUMERO_CANCHA_CAN}
                                  </h6>
                                  <p className="mb-0 text-muted">
                                    {partida.NOMBRE_SUCURSAL_SUC}
                                  </p>
                                </div>
                                <div className="d-flex align-items-center">
                                  <button
                                    type="button"
                                    className={`btn ${
                                      partida.YA_REGISTRADO
                                        ? "btn-outline-primary"
                                        : "btn-success"
                                    }`}
                                    disabled={
                                      Boolean(partida.YA_REGISTRADO) ||
                                      confirmingMatchId === partida.ID_MAT
                                    }
                                    onClick={() =>
                                      confirmarAsistencia(partida.ID_MAT)
                                    }
                                  >
                                    {confirmingMatchId === partida.ID_MAT ? (
                                      <span
                                        className="spinner-border spinner-border-sm"
                                        aria-hidden="true"
                                      ></span>
                                    ) : partida.YA_REGISTRADO ? (
                                      "Ya registrado"
                                    ) : (
                                      "Confirmar asistencia"
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card border radius-12 mt-4">
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                  <h5 className="mb-1">Historial de escaneados</h5>
                  <p className="text-muted mb-0">
                    Ultimos jugadores registrados en pase de lista.
                  </p>
                </div>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={cargarHistorial}>
                  Actualizar
                </button>
              </div>

              {isLoadingHistory ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status" aria-hidden="true"></div>
                </div>
              ) : historial.length === 0 ? (
                <div className="text-center py-4">
                  <i className="ri-list-check-3 text-muted" style={{ fontSize: 34 }}></i>
                  <p className="mt-2 mb-0 text-muted">Aun no hay jugadores escaneados.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table bordered-table mb-0">
                    <thead>
                      <tr>
                        <th>JUGADOR</th>
                        <th>PARTIDA</th>
                        <th>CANCHA</th>
                        <th>ESCANEADO POR</th>
                        <th>FECHA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historial.map((item) => {
                        const jugador = [item.user?.nombre, item.user?.apellido].filter(Boolean).join(" ") || "Jugador";
                        const escaneador = [item.atendidoPor?.nombre, item.atendidoPor?.apellido].filter(Boolean).join(" ") || "Staff";
                        const match = item.match || {};
                        const cancha = match.cancha?.nombre || "Cancha";
                        const sucursal = match.sucursal?.nombre || "Sucursal";
                        const fechaPartida = match.fecha ? new Date(match.fecha) : null;
                        const finPartida = fechaPartida ? new Date(fechaPartida.getTime() + (match.duracion || 90) * 60000) : null;
                        return (
                          <tr key={item.id}>
                            <td>
                              <div className="fw-semibold">{jugador}</div>
                              {item.user?.telefono && <div className="text-muted small">{item.user.telefono}</div>}
                            </td>
                            <td>
                              <div>{match.titulo || "Partida"}</div>
                              <div className="text-muted small">
                                {fechaPartida ? `${formatTime(fechaPartida.toTimeString())} - ${formatTime(finPartida.toTimeString())}` : ""}
                              </div>
                            </td>
                            <td>
                              <div>{cancha}</div>
                              <div className="text-muted small">{sucursal}</div>
                            </td>
                            <td>{escaneador}</td>
                            <td>{formatDateTime(item.fechaCheckin)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default PaseListaPartidas;
